/**
 * Shared `/api/me` loader. Distinguishes:
 *   - pending (first call hasn't returned yet)
 *   - signedIn-but-no-org (Clerk session without an active org)
 *   - unauthorized (no valid Clerk secret on the Worker)
 *   - ok (have a user + org)
 *
 * Fixes the "Loading… forever" bug caused by using a single nullable state for
 * both "still loading" and "loaded, empty".
 */

import { useEffect, useState } from 'react';
import { useApi, type MeResponse } from './api';

export type MeStatus =
  | { state: 'pending' }
  | { state: 'worker-down'; detail?: string }
  | { state: 'unauthorized'; error: string; detail?: string; hint?: string }
  | { state: 'no-org'; me: MeResponse }
  | { state: 'ok'; me: MeResponse };

interface ApiErrorData {
  error?: string;
  detail?: string;
  hint?: string;
}

export function useMe(): MeStatus {
  const api = useApi();
  const [status, setStatus] = useState<MeStatus>({ state: 'pending' });

  useEffect(() => {
    let cancelled = false;
    api.get<MeResponse>('/api/me')
      .then((me) => {
        if (cancelled) return;
        setStatus(me.org ? { state: 'ok', me } : { state: 'no-org', me });
      })
      .catch((err: Error & { status?: number; data?: ApiErrorData }) => {
        if (cancelled) return;
        const data = err.data ?? {};
        const errorCode = data.error ?? err.message ?? '';

        // 504 / aborted fetch / network error → Worker is unreachable.
        if (
          err.status === 504 ||
          errorCode === 'worker_timeout' ||
          /failed to fetch|network/i.test(err.message ?? '')
        ) {
          setStatus({ state: 'worker-down', detail: data.detail ?? err.message });
          return;
        }

        setStatus({
          state: 'unauthorized',
          error: errorCode || 'unauthorized',
          detail: data.detail,
          hint: data.hint,
        });
      });
    return () => { cancelled = true; };
  }, [api]);

  return status;
}
